import db from '@/db';

export const PAYMENT_METHOD_KEYS = ['Dinheiro', 'Pix', 'Crédito', 'Débito', 'Voucher'];

/**
 * Agrega dados do dia a partir das comandas pagas (excluindo treinamento).
 * @param {Date} [refDate] - data de referência (default: hoje)
 * @returns {Promise<DailyCloseReport>}
 */
export async function buildDailyCloseReport(refDate = new Date()) {
  const start = new Date(refDate);
  start.setHours(0, 0, 0, 0);
  const end = new Date(refDate);
  end.setHours(23, 59, 59, 999);
  const startIso = start.toISOString();
  const endIso = end.toISOString();

  const all = await db.commands.where('status').equals('paid').toArray();
  const paidToday = all.filter((c) => {
    if (c.isTraining) return false;
    const t = c.paidAt || c.closedAt;
    return t && t >= startIso && t <= endIso;
  });

  // Init breakdown
  const byMethod = PAYMENT_METHOD_KEYS.reduce((acc, k) => {
    acc[k] = { total: 0, count: 0 };
    return acc;
  }, {});
  // Tolerar "Cartão" antigo (registros pré-multimodal) agregando em Crédito
  let legacyCardTotal = 0;

  let grandTotal = 0;
  let coffeeTotal = 0;
  let coffeeCount = 0;         // quantidades (itens) de café
  let coffeeCommandsCount = 0; // # de comandas que tinham pelo menos 1 item de café
  let totalService = 0;
  let totalKgSold = 0;         // Fase 24 — soma de weightEntries[].weight
  const itemAgg = {}; // name -> { count, total }

  for (const c of paidToday) {
    const baseTotal = Number(c.total || 0);
    const service = Number(c.serviceCharge || 0);
    const finalTotal = Number(c.finalTotal ?? baseTotal + service);
    grandTotal += finalTotal;
    totalService += service;
    const method = c.paymentMethod;
    if (byMethod[method]) {
      byMethod[method].total += finalTotal;
      byMethod[method].count += 1;
    } else if (method === 'Cartão') {
      legacyCardTotal += finalTotal;
      byMethod['Crédito'].total += finalTotal;
      byMethod['Crédito'].count += 1;
    }

    // Fase 24 — Peso total vendido (do modo balança)
    if (Array.isArray(c.weightEntries)) {
      for (const w of c.weightEntries) {
        totalKgSold += Number(w.weight || 0);
      }
    }

    let hasCoffeeItem = false;
    for (const it of c.items || []) {
      const name = it.name || 'Item';
      if (!itemAgg[name]) itemAgg[name] = { count: 0, total: 0 };
      itemAgg[name].count += Number(it.quantity || 1);
      itemAgg[name].total += Number(it.subtotal || 0);
      if (it.isCoffee) {
        hasCoffeeItem = true;
        coffeeTotal += Number(it.subtotal || 0);
        coffeeCount += Number(it.quantity || 1);
      }
    }
    if (hasCoffeeItem) coffeeCommandsCount += 1;

    // Buffet como linha agregada (não por item individual)
    if (c.buffetTotal && c.buffetTotal > 0) {
      const bname = 'Buffet (por Kg)';
      if (!itemAgg[bname]) itemAgg[bname] = { count: 0, total: 0 };
      itemAgg[bname].count += 1;
      itemAgg[bname].total += Number(c.buffetTotal);
    }
  }

  const topItems = Object.entries(itemAgg)
    .map(([name, v]) => ({ name, count: v.count, total: v.total }))
    .sort((a, b) => b.count - a.count || b.total - a.total)
    .slice(0, 3);

  const regularCommandsCount = Math.max(0, paidToday.length - coffeeCommandsCount);

  return {
    date: start,
    totalRevenue: grandTotal,
    totalService,
    totalCommands: paidToday.length,
    regularCommandsCount,
    coffeeCommandsCount,
    totalKgSold,
    coffee: { total: coffeeTotal, count: coffeeCount, commands: coffeeCommandsCount },
    byMethod,
    legacyCardTotal,
    topItems,
  };
}

function fmtBR(n) {
  return Number(n || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

/**
 * Formata peso em kg com 3 casas decimais (pt-BR).
 * Ex: 12.345 → "12,345 kg". Se for pesado em gramas residuais, ajusta
 * para "998 g" quando < 1 kg.
 */
export function formatKg(kg) {
  const v = Number(kg || 0);
  if (v > 0 && v < 1) {
    return `${Math.round(v * 1000)} g`;
  }
  return `${v.toLocaleString('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} kg`;
}

export function formatReportDateBR(date = new Date()) {
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

const METHOD_EMOJI = {
  Dinheiro: '💵',
  Pix: '📱',
  'Crédito': '💳',
  'Débito': '💳',
  Voucher: '🎫',
};

/**
 * Gera texto formatado para WhatsApp (bold com *asterisco*, bullets simples).
 */
export function buildWhatsAppText(report) {
  const dateStr = formatReportDateBR(report.date);
  const lines = [];
  lines.push(`*KAVEN RESTAURANTE - Fechamento ${dateStr}*`);
  lines.push('');
  lines.push(`💰 *Faturamento Total:* ${fmtBR(report.totalRevenue)}`);
  lines.push(`📋 *Comandas Pagas:* ${report.totalCommands} (${report.regularCommandsCount} buffet/bebida + ${report.coffeeCommandsCount} café)`);
  lines.push(`⚖️ *Peso Vendido:* ${formatKg(report.totalKgSold || 0)}`);
  if (report.totalService > 0) {
    lines.push(`🎩 *Gorjetas (10% Serviço):* ${fmtBR(report.totalService)}`);
  }
  lines.push('');
  lines.push('*Por Método:*');
  for (const k of PAYMENT_METHOD_KEYS) {
    const m = report.byMethod[k];
    lines.push(`${METHOD_EMOJI[k]} ${k}: ${fmtBR(m.total)} (${m.count})`);
  }
  lines.push('');
  lines.push(`☕ *Receita Café da Manhã:* ${fmtBR(report.coffee.total)} (${report.coffee.count} un)`);
  lines.push('');
  lines.push('*Top 3 Itens:*');
  if (report.topItems.length === 0) {
    lines.push('— Sem itens no período —');
  } else {
    report.topItems.forEach((it, i) => {
      lines.push(`${i + 1}. ${it.name} (${it.count}x) — ${fmtBR(it.total)}`);
    });
  }
  lines.push('');
  lines.push('_Gerado pelo Sistema Kaven_');
  return lines.join('\n');
}

export function openWhatsAppShare(report) {
  const text = buildWhatsAppText(report);
  const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
  window.open(url, '_blank', 'noopener,noreferrer');
}
